import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { RANGE_PRESETS, presetRange, utcDay, type RangeChoice, type RangePreset } from './format'
import type { Range } from './types'

/** A button that opens a small panel below it; a click outside or Escape closes it. */
export function Popover({
  label,
  icon,
  children,
  align = 'left',
  className = '',
}: {
  label: ReactNode
  icon?: string
  children: (close: () => void) => ReactNode
  align?: 'left' | 'right'
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const id = useId()
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <div className={`tl-pop ${className}`} ref={root}>
      <button type="button" className="dl-btn" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
        {icon && <FeatherIcon name={icon} size={14} />} {label}
      </button>
      {open && (
        <div id={id} className={`tl-pop-panel tl-pop-panel--${align}`} role="dialog">
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  )
}

/** The date range: a preset, or custom days. Days are UTC, like every money figure. */
export function RangePicker({ value, onChange }: { value: RangeChoice; onChange: (next: RangeChoice) => void }) {
  const { t } = useTranslation('trackingLinks')
  const ids = useId()
  const pick = (preset: RangePreset) => {
    if (preset !== 'custom') return onChange({ ...value, preset })
    // Custom starts from what was on screen, so switching to it changes nothing yet.
    const shown = presetRange(value.preset)
    onChange({ preset, custom: { from: shown.from ?? value.custom.from, to: shown.to ?? value.custom.to ?? utcDay(new Date()) } })
  }
  const setDay = (part: keyof Range, day: string) => onChange({ ...value, custom: { ...value.custom, [part]: day || null } })
  return (
    <div className="tl-range">
      <label className="dl-label" htmlFor={`${ids}-preset`}>{t('range.label')}</label>
      <select id={`${ids}-preset`} className="dl-input tl-range-select" value={value.preset} onChange={(e) => pick(e.target.value as RangePreset)}>
        {RANGE_PRESETS.map((p) => (
          <option key={p} value={p}>{t(`range.${p}`)}</option>
        ))}
      </select>
      {value.preset === 'custom' && (
        <span className="tl-dates">
          <input type="date" className="dl-input" value={value.custom.from ?? ''} max={value.custom.to ?? undefined} aria-label={t('range.from')}
            onChange={(e) => setDay('from', e.target.value)} />
          <span className="dl-muted">–</span>
          <input type="date" className="dl-input" value={value.custom.to ?? ''} min={value.custom.from ?? undefined} aria-label={t('range.to')}
            onChange={(e) => setDay('to', e.target.value)} />
        </span>
      )}
    </div>
  )
}
